import React, { useState, useEffect } from 'react';

import 'mapbox-gl/dist/mapbox-gl.css';

import { createMapClickHandler, createPointClickHandler } from './MapEventHandlers';
import { updateConnections } from './ConnectionRenderer';
import { updateDurationLabels } from './DurationLabels';

import { 
  shouldShowAutoPath, 
  shouldShowCustomPath, 
  getMapContainerClassName
} from '../Services/MapServices/MapService';

import {
  isMapClickAddPointEnabled,
  isPointSelectionEnabled
} from '../Services/MapServices/InteractionService';

import { useMapState } from './hooks/useMapState';
import { useUnifiedPathManager } from './hooks/useUnifiedPathManager';
import { useMto } from '../context/MtoContext';
import './styles/index.js';
import LayerControl from './LayerControl';

// Main map component for the MTO system
export default function MapComponent() {
  // Get state and functions from MTO context
  const {
    currentMode,
    setupSubmode,
    locations,
    startId,
    setLocations,
    setStartId,
    setPath,
    visiblePaths: paths,
    customVehicleCount,
    mapPointSelectCallback,
    setMapRef,
    connections
  } = useMto();
  
  const [mapError, setMapError] = useState(null);
  
  // Extract auto and custom paths
  const autoPath = paths.auto || [];
  const customPath = paths.custom || [];
  
  // Determine which paths should be visible based on current mode
  const showAutoPath = shouldShowAutoPath(currentMode);
  const showCustomPath = shouldShowCustomPath(currentMode);

  // Initialize map state and path management
  const {
    mapContainer,
    map,
    mapReady,
    sourcesReadyRef,
    isMountedRef
  } = useMapState();
  
  // Register map reference and ensure layers stay visible
  useEffect(() => {
    if (map.current && mapReady) {
      setMapRef(map.current);
      
      // Make sure location layers stay visible
      setTimeout(() => {
        if (map.current && map.current.loaded()) {
          try {
            if (map.current.getLayer('points-circle-layer')) {
              map.current.setLayoutProperty('points-circle-layer', 'visibility', 'visible');
            }
            if (map.current.getLayer('points-symbol-layer')) {
              map.current.setLayoutProperty('points-symbol-layer', 'visibility', 'visible');
            }
          } catch (error) {
            console.warn('Error ensuring layer visibility:', error);
          }
        }
      }, 100);
    }
  }, [map, mapReady, setMapRef]);
  
  // Unified path management
  const pathManagerState = useUnifiedPathManager({
    map,
    mapReady,
    sourcesReadyRef,
    isMountedRef,
    
    // Path data
    autoPath,
    customPath,
    locations,
    startId,
    
    // Display controls
    showAutoPath,
    showCustomPath
  });

  // Log path state for debugging
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && pathManagerState) {
      console.log('Path manager state:', {
        isReady: pathManagerState.isReady,
        hasAutoPath: pathManagerState.hasAutoPath,
        currentMode
      });
    }
  }, [pathManagerState, currentMode]);

  // Update connections on map
  useEffect(() => {
    if (map.current && mapReady && sourcesReadyRef.current) {
      // Wait for map to be fully loaded
      setTimeout(() => {
        if (map.current && map.current.loaded()) {
          console.log('Updating connections and labels');
          
          updateConnections({
            map: map.current,
            mapReady,
            sourcesReady: sourcesReadyRef.current,
            connections
          });

          // Show duration labels on connections
          updateDurationLabels({
            map: map.current,
            mapReady,
            sourcesReady: sourcesReadyRef.current,
            connections,
            shouldShowDurationLabels: connections.length > 0
          });
        }
      }, 200);
    }
  }, [map, mapReady, connections]);

  // Keep location layers visible when switching modes
  useEffect(() => {
    // Fix layer visibility after mode changes
    if (map.current && mapReady && locations.length > 0) {
      setTimeout(() => {
        try {
          if (map.current.getLayer('points-circle-layer')) {
            map.current.setLayoutProperty('points-circle-layer', 'visibility', 'visible');
          }
          if (map.current.getLayer('points-symbol-layer')) {
            map.current.setLayoutProperty('points-symbol-layer', 'visibility', 'visible');
          }
        } catch (error) {
          console.warn('Error fixing layer visibility:', error);
        }
      }, 50);
    }
  }, [currentMode, map, mapReady, locations]);

  // Create event handlers only if map is ready
  const mapClickHandler = createMapClickHandler(
    map.current, 
    locations, 
    currentMode, 
    setupSubmode, 
    setLocations
  );
  
  const pointClickHandler = createPointClickHandler(
    map.current, 
    locations, 
    startId, 
    currentMode, 
    setStartId, 
    mapPointSelectCallback
  );
  
  // Add event listeners when map is ready
  useEffect(() => {
    if (!map.current || !mapReady) return;
    
    // Clean up previous listeners to avoid duplicates
    if (map.current._hasClickListeners) {
      map.current.off('click', map.current._mapClickHandler);
      map.current.off('click', map.current._pointClickHandler);
      map.current._hasClickListeners = false;
    }
    
    // Store handlers for later cleanup
    map.current._mapClickHandler = mapClickHandler;
    map.current._pointClickHandler = pointClickHandler;
    
    // Add map click handler if adding points by clicking is enabled
    if (isMapClickAddPointEnabled(currentMode, setupSubmode)) {
      console.log('Adding map click handler');
      map.current.on('click', mapClickHandler);
    }
    // Add point selection handler if needed
    const pointSelectionEnabled = isPointSelectionEnabled(currentMode);
    console.log('Point selection enabled:', pointSelectionEnabled);
    console.log('Map callback:', mapPointSelectCallback);
    console.log('Current mode:', currentMode);
    
    if (pointSelectionEnabled && mapPointSelectCallback) {
      console.log('Adding point selection handler');
        // Attach point click handler
      map.current.on('click', pointClickHandler);
    } else {
      console.log('Point selection not enabled');
    }
    
    // Track that listeners are active
    map.current._hasClickListeners = true;
    
    // Remove listeners on cleanup
    return () => {
      if (map.current) {
        // Remove event listeners
        map.current.off('click', map.current._mapClickHandler);
        map.current.off('click', map.current._pointClickHandler);
        map.current._hasClickListeners = false;
      }
    };
  }, [map, mapReady, currentMode, setupSubmode, mapClickHandler, pointClickHandler, mapPointSelectCallback]);
  
  // Get container class name with correct parameters
  const containerClassName = getMapContainerClassName(currentMode, setupSubmode);
  
  return (
    <div className={containerClassName} style={{position: 'relative', width: '100%', height: '100%'}}>
      {mapError && (
        <div className="map-error">
          <p>Error loading map: {mapError}</p>
        </div>
      )}

      <div 
        ref={mapContainer} 
        className="mto-map-container" 
        style={{width: '100%', height: '100%', position: 'absolute'}}
      />

      {/* Layer Control Panel */}
      <LayerControl 
        map={map.current}
        mapReady={mapReady}
        currentMode={currentMode}
      />

    </div>
  );
} 